import { NODE_TYPE } from '../constants';

export const treeWalker = (
  node,
  callback,
  { maxDepth = Infinity, currentDepth = 0 } = {}
) => {
  if (!node) {
    return;
  }
  if (callback(node, currentDepth)) {
    return;
  }
  if (!node.children) {
    return;
  }
  if (maxDepth > 0 && node.children.length) {
    for (let i = 0; i < node.children.length; i += 1) {
      treeWalker(node.children[i], callback, {
        maxDepth: maxDepth - 1,
        currentDepth: currentDepth + 1,
      });
    }
  }
};

export const findLeafNodes = (node) => {
  const children = node.children || [];
  if (children.length === 0) {
    return [node];
  }
  const flatMapOfChildren = children.flatMap(findLeafNodes);
  return [].concat(flatMapOfChildren);
};

export const findChildNodesByType = (node, nodeType, maxDepth = Infinity) => {
  let nodes = [];
  treeWalker(
    node,
    (currentNode) => {
      if (currentNode.node_type === nodeType) {
        nodes.push(currentNode);
      }
    },
    { maxDepth: maxDepth }
  );
  return nodes;
};

export const findSectionNode = (assemblage, sectionId, topicsOnly = false) => {
  let sectionNode = null;
  const TOPIC_NODE_DEPTH = 1;
  treeWalker(assemblage.node, (node, currentDepth) => {
    if (
      node.node_type === NODE_TYPE.SECTION &&
      node.value_id === sectionId &&
      (!topicsOnly || currentDepth === TOPIC_NODE_DEPTH)
    ) {
      sectionNode = node;
      return true;
    }
  });
  return sectionNode;
};

export const findTopicNodes = (assemblage) => {
  let topicNodes = [];
  const TOPIC_NODE_DEPTH = 1;

  treeWalker(assemblage.node, (currentNode, currentDepth) => {
    if (
      currentNode.node_type === NODE_TYPE.SECTION &&
      currentDepth === TOPIC_NODE_DEPTH
    ) {
      topicNodes.push(currentNode);
    }
  });

  return topicNodes;
};

export const findTopicNode = (assemblage, topicId) => {
  const topicNodes = findTopicNodes(assemblage);
  return topicNodes.find((node) => node.value_id == topicId);
};

export const isCourseFeatureEnabledInFeatureSet = (
  assemblage,
  featureSet,
  feature
) => {
  return assemblage.course.feature_sets[featureSet].includes(feature);
};

// TODO deprecate now that classDetail can be loaded everywhere
export const isStudentNotesEnabled = (assemblage) => {
  return assemblage.course.show_student_notes;
};

export const getFlatLessons = (assemblage) => {
  const lessonNodes = findChildNodesByType(assemblage.node, NODE_TYPE.LESSON);
  return lessonNodes.map((lessonNode) => {
    return assemblage.content.lessons[lessonNode.value_id];
  });
};

export const getPublishedFlatLessonsWithChunks = (assemblage) => {
  const flatLessons = getFlatLessons(assemblage);
  return flatLessons.filter(
    (lesson) =>
      lesson.is_published && lesson.chunk_count && !lesson.is_coming_soon
  );
};

export const getFirstLessonInCourse = (assemblage) => {
  return getFlatLessons(assemblage)[0];
};

export const getPrevAndNextLessonIds = (assemblage, lessonId) => {
  const flatLessons = getPublishedFlatLessonsWithChunks(assemblage);
  if (flatLessons.length === 0) {
    return [];
  }

  const currentLesson = flatLessons.find((l) => l.id === Number(lessonId));
  if (!currentLesson) {
    return [];
  }

  const currentLessonIndex = flatLessons.indexOf(currentLesson);
  const prevLessonIndex =
    currentLessonIndex === 0 ? null : currentLessonIndex - 1;
  const nextLessonIndex =
    currentLessonIndex === flatLessons.length - 1
      ? null
      : currentLessonIndex + 1;

  const prevLessonId =
    (prevLessonIndex != null && flatLessons[prevLessonIndex].id) || null;
  const nextLessonId =
    (nextLessonIndex != null && flatLessons[nextLessonIndex].id) || null;

  return {
    prevLessonId,
    nextLessonId,
  };
};

export const getPrevAndNextQuestionSetIds = (assemblage, examId) => {
  const flatLessons = getPublishedFlatLessonsWithChunks(assemblage);
  if (flatLessons.length === 0) {
    return [];
  }

  const currentLesson = flatLessons.find(
    (l) => l.question_set?.id === Number(examId)
  );
  if (!currentLesson) {
    return [];
  }

  const currentLessonIndex = flatLessons.indexOf(currentLesson);
  const prevLessonIndex =
    currentLessonIndex === 0 ? null : currentLessonIndex - 1;
  const nextLessonIndex =
    currentLessonIndex === flatLessons.length - 1
      ? null
      : currentLessonIndex + 1;

  const prevQuestionSetId =
    (prevLessonIndex != null &&
      flatLessons[prevLessonIndex].question_set?.id) ||
    null;
  const nextQuestionSetId =
    (nextLessonIndex != null &&
      flatLessons[nextLessonIndex].question_set?.id) ||
    null;

  return {
    prevQuestionSetId,
    nextQuestionSetId,
  };
};

export const getTopicLessons = (assemblage, topicId, publishedOnly = true) => {
  const topicNode = findSectionNode(assemblage, topicId, true);
  const lessonNodes = findChildNodesByType(topicNode, NODE_TYPE.LESSON);
  let lessons = lessonNodes.map(
    (lessonNode) => assemblage.content.lessons[`${lessonNode.value_id}`]
  );

  if (publishedOnly) {
    lessons = lessons.filter((lesson) => lesson.is_published);
  }

  return lessons;
};

// used by the Lesson component in studentCourseProgress to ensure that
// progress pages for courses with question sets are styled consistently
export const getCourseHasQuestionSets = (assemblage) => {
  const flatLessons = getFlatLessons(assemblage);
  return flatLessons.some((lesson) => lesson.question_set);
};

export const getTopics = (assemblage, publishedOnly = true) => {
  const topicNodes = findTopicNodes(assemblage);

  let topics = topicNodes.map(
    (topicNode) => assemblage.content.sections[`${topicNode.value_id}`]
  );
  if (publishedOnly) {
    topics = topics.filter((topic) => topic.is_published);
  }

  return topics;
};

// XXX the publishedOnly parameter here refers to the exams that are retrieved, and not the topics the exams are located in.
export const getTopicExamsForTopic = (
  assemblage,
  topicId,
  publishedOnly = true,
  additionalMaterialsOnly = false
) => {
  const topicNode = findTopicNodes(assemblage).find(
    (topicNode) => topicNode.value_id === topicId
  );

  let examNodes = findChildNodesByType(topicNode, NODE_TYPE.EXAM);
  if (additionalMaterialsOnly) {
    examNodes = examNodes.filter((examNode) => examNode.additional_materials);
  }

  let topicExams = examNodes.map(
    (examNode) => assemblage.content.exams[`${examNode.value_id}`]
  );
  if (publishedOnly) {
    topicExams = topicExams.filter((topicExam) => topicExam.is_published);
  }

  // topic exams should always be submittable (ie: EM exams and not question sets)
  topicExams = topicExams.filter((topicExam) => topicExam.is_submittable);

  return topicExams;
};

export const getTopicAttachmentsForTopic = (
  assemblage,
  topicId,
  additionalMaterialsOnly = false
) => {
  const topicNode = findTopicNodes(assemblage).find(
    (topicNode) => topicNode.value_id === topicId
  );

  let attachmentNodes = findChildNodesByType(
    topicNode,
    NODE_TYPE.ATTACHMENT,
    1 // don't recurse any deeper than the topic node
  );
  if (additionalMaterialsOnly) {
    attachmentNodes = attachmentNodes.filter(
      (attachmentNode) => attachmentNode.additional_materials
    );
  }

  const topicAttachments = attachmentNodes.map(
    (attachmentNode) =>
      assemblage.content.attachments[`${attachmentNode.value_id}`]
  );

  return topicAttachments;
};

export const getCourseAttachments = (
  assemblage,
  additionalMaterialsOnly = false
) => {
  let attachmentNodes = findChildNodesByType(
    assemblage.node,
    NODE_TYPE.ATTACHMENT,
    1 // don't recurse any deeper than the course node
  );
  if (additionalMaterialsOnly) {
    attachmentNodes = attachmentNodes.filter(
      (attachmentNode) => attachmentNode.additional_materials
    );
  }

  const courseAttachments = attachmentNodes.map(
    (attachmentNode) =>
      assemblage.content.attachments[`${attachmentNode.value_id}`]
  );

  return courseAttachments;
};

export const getHasCourseSuccessMeasure = (assemblage) => {
  const flatLessons = getFlatLessons(assemblage);
  return flatLessons.some((lesson) => lesson.has_success_measure);
};

// TODO untested: not sure there's much merit in keeping these
export const getCourse = (assemblage) => {
  return assemblage.course;
};

export const getAssemblageExam = (assemblage, examId) => {
  return assemblage.content.exams[`${examId}`];
};

export const getCourseExams = (assemblage) => {
  const courseExamNodes = assemblage.node.children.filter(
    (node) => node.node_type === 'EXAM'
  );

  const exams = courseExamNodes.map((node) => {
    return assemblage.content.exams[node.value_id];
  });

  return exams;
};
